<template>
    <div>
        <div class="vx-col w-full mb-base">
            <!-- start:outbound info -->
            <vs-card title="Detail" class="p-4 mb-6">
                <div class="flex flex-wrap items-center justify-between mb-10">
                    <div class="flex items-center">
                        <h4 class="font-medium">Outbound Info</h4>
                    </div>
                </div>

                <div class="vx-row mt-6">
                    <div class="vx-col md:w-1/3 w-full">
                        <div>
                            <label class="text-lg font-bold">Reference</label>
                            <p class="text-base">{{ outboundDetail.outbound_reference }}</p>
                        </div>

                        <div class="mt-4">
                            <label class="text-lg font-bold">Status</label>
                            <p class="mt-3">
                                <span class="px-5 py-2 text-sm font-semibold text-white  rounded-full"
                                    :class="[statusBadge.color]">{{ statusBadge.text }}</span>
                            </p>
                        </div>

                        <div class="mt-6">
                            <label class="text-lg font-bold">Delivery Order</label>
                            <p class="mt-2">{{ doLength || '-' }}</p>
                        </div>
                    </div>

                    <div class="vx-col md:w-1/3 w-full">
                        <label class="text-lg font-bold">Outstanding Products</label>
                        <div class="mt-2 flex flex-col gap-y-3">
                            <div v-for="(product, product_index) in products" :key="`product-${product_index}`"
                                class="flex items-center gap-x-4">
                                <p class="text-2xl font-medium flex items-center gap-4"><span
                                        class="total-product text-right">{{ product.total }}</span> <span
                                        class="font-light text-lg">x</span></p>

                                <div>
                                    <p class="text-xl">{{ product.code }}</p>
                                    <p class="text-sm font-light">{{ product.description_1 }} <span
                                            v-if="product.description_2">| {{ product.description_2 }}</span></p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="vx-col md:w-1/3 w-full">
                        <label class="text-lg font-bold">Available Products</label>
                        <div class="mt-2 flex flex-col gap-y-3">
                            <div v-for="(product, product_index) in products" :key="`product-${product_index}`"
                                class="flex items-center gap-x-4">
                                <p class="text-2xl font-medium flex items-center gap-4"><span
                                        class="total-product text-right">{{ product.total_base_qty }}</span> <span
                                        class="font-light text-lg">x</span></p>

                                <div>
                                    <p class="text-xl">{{ product.code }}</p>
                                    <p class="text-sm font-light">{{ product.description_1 }} <span
                                            v-if="product.description_2">| {{ product.description_2 }}</span></p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </vs-card>
            <!-- end:outbound info -->

            <!-- start:tabs -->
            <vs-card title="Item Check" class="p-4 mb-6">
                <div class="flex flex-wrap items-center justify-between mb-10">
                    <div class="flex items-center">
                        <h5 class="font-medium">Item Check</h5>
                    </div>
                </div>

                <div v-if="submittedProducts.length" class="vx-row mb-10">
                    <div class="vx-col md:w-1/3 w-full">
                        <label class="text-lg font-bold">Total Submitted Items</label>
                        <div class="mt-2 flex flex-col gap-y-3">
                            <div v-for="(product, product_index) in submittedProducts" :key="`submitted-product-${product_index}`"
                                class="flex items-center gap-x-4">
                                <p class="text-2xl font-medium flex items-center gap-4"><span
                                        class="total-product text-right">{{ product.total_qty }}</span> <span
                                        class="font-light text-lg">x</span></p>

                                <div>
                                    <p class="text-xl">{{ product.code }}</p>
                                    <p class="text-sm font-light">{{ product.description_1 }} <span
                                            v-if="product.description_2">| {{ product.description_2 }}</span></p>
                                </div>
                            </div>
                        </div>

                        <vs-button 
                            color="success" 
                            class="btn mt-5" 
                            @click="openCloseConfirmation"
                            :disabled="!commitInfo.scanned_items.length"
                        >
                            Close DO
                        </vs-button>
                    </div>
                </div>

                <vs-tabs :value="tabValue">
                    <vs-tab label="Form">
                        <div class="vx-row">
                            <div class="vx-col md:w-1/2 w-full">
                                <h6 class="mb-4 mt-4 text-lg font-bold">TV</h6>
                                <vs-table max-items="10" :data="productsWithSerial" stripe search
                                    style="overflow-y: auto;">
                                    <template slot="thead">
                                        <vs-th>Product</vs-th>
                                        <vs-th>Scan Serial Number</vs-th>
                                        <vs-th>Action</vs-th>
                                    </template>

                                    <template slot-scope="{data}">
                                        <vs-tr :data="item" v-for="(item, index) in data" :key="index"
                                            :v-show="item.status !== 'delete'">
                                            <vs-td>
                                                {{ item.code }}
                                            </vs-td>

                                            <vs-td>
                                                <vs-input class="w-full" v-model="scanInputs[item.code]"
                                                    @keyup.enter="() => submitItem(item)" />
                                            </vs-td>

                                            <vs-td style="width: 10%">
                                                <div class="flex gap-x-3">
                                                    <vs-button color="primary" class="btn"
                                                        @click="() => submitItem(item)"
                                                        :disabled="!scanInputs[item.code]">Submit</vs-button>
                                                </div>
                                            </vs-td>
                                        </vs-tr>
                                    </template>
                                </vs-table>
                            </div>

                            <div class="vx-col md:w-1/2 w-full">
                                <h6 class="mb-4 mt-4 text-lg font-bold">Bracket TV</h6>
                                <vs-table max-items="10" :data="productsWithoutSerial" stripe search
                                    style="overflow-y: auto;">
                                    <template slot="thead">
                                        <vs-th>Product</vs-th>
                                        <vs-th>Qty</vs-th>
                                        <vs-th>Open Qty</vs-th>
                                        <vs-th>Action</vs-th>
                                    </template>

                                    <template slot-scope="{data}">
                                        <vs-tr :data="item" v-for="(item, index) in data" :key="index"
                                            :v-show="item.status !== 'delete'">
                                            <vs-td>
                                                {{ item.code }}
                                            </vs-td>

                                            <vs-td style="width: 10%">
                                                <vs-input class="w-full" v-model="qtyInputs[item.code]" />
                                            </vs-td>

                                            <vs-td style="width: 10%">
                                                <vs-input class="w-full" :value="item.total" disabled />
                                            </vs-td>

                                            <vs-td style="width: 10%">
                                                <div class="flex gap-x-3">
                                                    <vs-button color="primary" class="btn"
                                                        @click="() => submitItem(item)"
                                                        :disabled="!(qtyInputs[item.code]) || (!item.total)">Submit</vs-button>
                                                </div>
                                            </vs-td>
                                        </vs-tr>
                                    </template>
                                </vs-table>
                            </div>
                        </div>
                    </vs-tab>

                    <vs-tab label="Submitted Items">
                        <div class="vx-row">
                            <div class="vx-col md:w-1/2 w-full">
                                <h6 class="mb-4 mt-4 text-lg font-bold">TV</h6>
                                <p class="text-base text-gray-500 mb-4">Total Submitted: {{ submittedItemsWithSerial.length }}</p>
                                <vs-table max-items="10" :data="submittedItemsWithSerial" stripe pagination search>
                                    <template slot="thead">
                                        <vs-th>Product</vs-th>
                                        <vs-th>Description</vs-th>
                                        <vs-th>Serial Number</vs-th>
                                        <vs-th>Qty</vs-th>
                                        <vs-th>Action</vs-th>
                                    </template>

                                    <template slot-scope="{data}">
                                        <vs-tr :data="item" v-for="(item, index) in data" :key="index"
                                            :v-show="item.status !== 'delete'">
                                            <vs-td>
                                                {{ item.product_code }}
                                            </vs-td>

                                            <vs-td>
                                                {{ item.product_description_1 }}
                                            </vs-td>

                                            <vs-td>
                                                {{ item.pack_id }}
                                            </vs-td>

                                            <vs-td>
                                                {{ item.qty }}
                                            </vs-td>

                                            <vs-td style="width: 10%">
                                                <vs-button color="danger" class="btn" icon-pack="feather"
                                                    icon="icon-trash-2" @click="() => openCancelConfirmation(item)">
                                                    Cancel
                                                </vs-button>
                                            </vs-td>
                                        </vs-tr>
                                    </template>
                                </vs-table>
                            </div>

                            <div class="vx-col md:w-1/2 w-full">
                                <h6 class="mb-4 mt-4 text-lg font-bold">Bracket TV</h6>
                                <p class="text-base text-gray-500 mb-4">Total Submitted: {{ submittedItemsWithoutSerial.length }}</p>
                                <vs-table max-items="10" :data="submittedItemsWithoutSerial" stripe pagination search>
                                    <template slot="thead">
                                        <vs-th>Product</vs-th>
                                        <vs-th>Description</vs-th>
                                        <vs-th>Qty</vs-th>
                                        <vs-th>Action</vs-th>
                                    </template>

                                    <template slot-scope="{data}">
                                        <vs-tr :data="item" v-for="(item, index) in data" :key="index"
                                            :v-show="item.status !== 'delete'">
                                            <vs-td>
                                                {{ item.product_code }}
                                            </vs-td>

                                            <vs-td>
                                                {{ item.product_description_1 }}
                                            </vs-td>

                                            <vs-td>
                                                {{ item.qty }}
                                            </vs-td>

                                            <vs-td style="width: 10%">
                                                <vs-button color="danger" class="btn" icon-pack="feather"
                                                    icon="icon-trash-2" @click="() => openCancelConfirmation(item)">
                                                    Cancel
                                                </vs-button>
                                            </vs-td>
                                        </vs-tr>
                                    </template>
                                </vs-table>
                            </div>
                        </div>
                    </vs-tab>

                    <vs-tab label="Delivery Orders">
                        <vs-table max-items="10" :data="commitInfo.has_delivery_orders" stripe search>
                            <template slot="thead">
                                <vs-th>DO Reference</vs-th>
                                <vs-th>Status</vs-th>
                                <vs-th>Driver</vs-th>
                                <vs-th>No. Kendaraan</vs-th>
                                <vs-th>Action</vs-th>
                            </template>

                            <template slot-scope="{data}">
                                <vs-tr :data="item" v-for="(item, index) in data" :key="index"
                                    :v-show="item.status !== 'delete'">
                                    <vs-td>
                                        {{ item.do_reference }}
                                    </vs-td>

                                    <vs-td>
                                        <span
                                            class="px-5 py-2 text-sm font-semibold text-white bg-success rounded-full">{{
                                                item.status_name }}</span>
                                    </vs-td>
                                    
                                    <vs-td>
                                        <vs-input v-model="drivers[item.id]" @input="val => drivers[item.id] = val.charAt(0).toUpperCase() + val.slice(1)" />
                                    </vs-td>

                                    <vs-td>
                                        <vs-input v-model="vehicleCodes[item.id]" @input="val => vehicleCodes[item.id] = val.toUpperCase()" />
                                    </vs-td>

                                    <vs-td style="width: 10%">
                                        <vs-button color="primary" class="btn" icon-pack="feather" icon="icon-printer"
                                            @click="() => printDO(item)"
                                            :disabled="!drivers[item.id] || !vehicleCodes[item.id]">
                                            Print
                                        </vs-button>
                                    </vs-td>
                                </vs-tr>
                            </template>
                        </vs-table>
                    </vs-tab>
                </vs-tabs>
            </vs-card>
            <!-- end:tabs -->
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            maxRetry: 10,

            commitInfo: {
                scanned_items: []
            },
            outboundDetail: {},

            tabValue: 0,
            isLoading: false,

            // selectedProducts: [],
            scanInputs: {},
            qtyInputs: {},
            drivers: {},
            vehicleCodes: {}
        }
    },

    computed: {
        doLength() {
            const { has_delivery_orders } = this.commitInfo

            if (!has_delivery_orders) return 0

            return has_delivery_orders.length
        },

        products() {
            const { outboundDetail } = this

            if (!outboundDetail || !outboundDetail.details) return []

            return (outboundDetail.details || []).reduce((products, item) => {
                const existed_product_index = products.findIndex(product => product.code == (item.product || {}).product_code)

                if (existed_product_index != -1) {
                    products[existed_product_index].items.push(item)
                    products[existed_product_index].total += parseInt(`${item.open_qty}`)
                    products[existed_product_index].total_base_qty += parseInt(`${item.inventory_qty}`)
                } else {
                    products.push({
                        total: parseInt(`${item.open_qty}`),
                        total_base_qty: parseInt(`${item.inventory_qty}`),
                        code: (item.product || {}).product_code,
                        items: [item],
                        description_1: item.product.description_1,
                        description_2: item.product.description_2,
                        need_serial_number: item.need_serial_number,
                        outbound_detail_id: item.id
                    })
                }

                return [...products]
            }, [])
        },

        productsWithSerial() {
            return this.products.filter(item => item.need_serial_number)
        },

        productsWithoutSerial() {
            return this.products.filter(item => !item.need_serial_number)
        },

        submittedItemsWithSerial() {
            return (this.commitInfo.scanned_items || []).filter(item => item.pack_id)
        },

        submittedItemsWithoutSerial() {
            return (this.commitInfo.scanned_items || []).filter(item => !item.pack_id)
        },

        submittedProducts() {
            const allSubmitted = [
                ...this.submittedItemsWithSerial,
                ...this.submittedItemsWithoutSerial
            ]

            return allSubmitted.reduce((products, item) => {
                const existedIndex = products.findIndex(p => p.code === item.product_code)

                if (existedIndex !== -1) {
                    products[existedIndex].total_qty += parseInt(`${item.qty}`)
                } else {
                    products.push({
                        code: item.product_code,
                        description_1: item.product_description_1,
                        description_2: item.product_description_2,
                        total_qty: parseInt(`${item.qty}`)
                    })
                }

                return [...products]
            }, [])
        },

        statusBadge() {
            const { outboundDetail } = this
            let color = 'bg-grey'

            if (outboundDetail.status) {
                switch ((outboundDetail.status || {}).name) {
                    case 'Fully Committed': {
                        color = 'bg-success'
                    }; break;
                    case 'Partially Committed': {
                        color = 'bg-warning'
                    }
                }
            }

            return {
                color,
                text: (outboundDetail.status || {}).name
            }
        }
    },

    watch: {
        isLoading(val) {
            if (val) this.$vs.loading();
            else this.$vs.loading.close();
        },

        products(newVal) {
            newVal.forEach(product => {
                this.qtyInputs[product.code] = product.need_serial_number ? '1' : `${product.total}`
            })
        },

        'commitInfo.has_delivery_orders'(newVal) {
            if (newVal) {
                newVal.forEach(item => {
                    if (this.drivers[item.id] === undefined) {
                        this.$set(this.drivers, item.id, item.driver)
                    } else if (this.drivers[item.id] === undefined) {
                        this.$set(this.drivers, item.id, '')
                    }

                    if (this.vehicleCodes[item.id] === undefined) {
                        this.$set(this.vehicleCodes, item.id, item.vehicle_code)
                    } else if (this.vehicleCodes[item.id] === undefined) {
                        this.$set(this.vehicleCodes, item.id, '')
                    }
                })
            }
        }
    },

    methods: {
        async submitItem(item) {
            const { warehouse_id, outbound_id } = this.$route.params
            const qty = this.qtyInputs[item.code]

            if (!qty) {
                this.$vs.notify({
                    color: "danger",
                    title: "Submit gagal",
                    iconPack: "feather",
                    icon: "icon-alert-triangle",
                    text: "Qty tidak bisa kosong"
                })
                return;
            }

            const request = {
                warehouse_id,
                outbound_id,
                outbound_detail_id: item.outbound_detail_id,
                serial_number: item.need_serial_number ? this.scanInputs[item.code] : null,
                qty
            }

            try {
                this.isLoading = true

                const res = await this.storeDODetail(request)

                if (res && res.data && res.data.success) {
                    this.commitInfo = res.data

                    this.$vs.notify({
                        color: "success",
                        title: "Submit berhasil",
                        iconPack: "feather",
                        icon: "icon-check",
                        text: "Produk terkait berhasil disubmit"
                    })

                    this.scanInputs[item.code] = ''

                    const commitInfoRes = await this.searchByOutbound()
                    if (commitInfoRes && commitInfoRes.data) {
                        this.commitInfo = commitInfoRes.data
                        this.outboundDetail = commitInfoRes.data.outbound || {}
                    }

                    this.isLoading = false
                }
            } catch (error) {
                this.handleError(error)
                this.isLoading = false
            }
        },

        storeDODetail(request) {
            return this.$http.post(`/tvd/outbound/deliveryorder/store`, {
                warehouse_id: request.warehouse_id,
                outbound_id: request.outbound_id,
                outbound_detail_id: request.outbound_detail_id,
                serial_number: request.serial_number,
                qty: request.qty
            })
        },

        searchByOutbound() {
            const { outbound_id, warehouse_id } = this.$route.params

            return this.$http.get(`/tvd/outbound/deliveryorder/searchByOutbound/${outbound_id}`, {
                params: {
                    warehouse_id
                }
            })
        },

        fetchAllData() {
            this.isLoading = true
            this.maxRetry = this.maxRetry - 1

            this.searchByOutbound()
                .then(commitInfoRes => {
                    if (commitInfoRes && commitInfoRes.data) {
                        this.commitInfo = commitInfoRes.data
                        this.outboundDetail = commitInfoRes.data.outbound || {}
                    }
                    this.isLoading = false
                })
                .catch(() => {
                    if (this.maxRetry < 0) {
                        this.isLoading = false
                        this.$vs.dialog({
                            type: 'confirm',
                            color: 'danger',
                            title: 'Server Error',
                            text: 'Try again?',
                            accept: () => {
                                this.fetchAllData()
                                this.maxRetry = 10
                            },
                            cancel: () => this.$router.go(-1),
                            acceptText: 'Yes',
                            cancelText: 'No'
                        })
                        return;
                    }

                    this.fetchAllData()

                    this.isLoading = false
                })
        },

        cancelItem(item) {
            this.isLoading = true

            this.$http.delete(`/tvd/outbound/deliveryorder/destroyDoDetail/${item.id}`)
                .then(async res => {
                    if (res && res.data && res.data.success) {
                        this.$vs.notify({
                            color: "success",
                            title: "Cancel Submit berhasil",
                            iconPack: "feather",
                            icon: "icon-check",
                            text: "Produk terkait berhasil dibatalkan"
                        })

                        const commitInfoRes = await this.searchByOutbound()
                        if (commitInfoRes && commitInfoRes.data) {
                            this.commitInfo = commitInfoRes.data
                            this.outboundDetail = commitInfoRes.data.outbound || {}
                        }

                        this.isLoading = false
                    }
                })
                .catch(error => {
                    this.handleError(error)
                    this.isLoading = false
                })
        },

        openCancelConfirmation(item) {
            this.$vs.dialog({
                type: 'confirm',
                color: 'danger',
                title: 'Confirmation',
                text: 'Are you sure to cancel the submitted items?',
                accept: () => this.cancelItem(item),
                acceptText: 'Yes',
                cancelText: 'No'
            })
        },

        openCloseConfirmation() {
            this.$vs.dialog({
                type: 'confirm',
                color: 'danger',
                title: 'Confirmation',
                text: 'Are you sure to close the submission of this item?',
                accept: () => this.closeDO(),
                acceptText: 'Yes',
                cancelText: 'No'
            })
        },

        closeDO() {
            this.isLoading = true

            const { warehouse_id, outbound_id } = this.$route.params

            const submittedItem = this.commitInfo.scanned_items[0]

            if (!submittedItem) {
                this.$vs.notify({
                    color: "danger",
                    title: "Failed to close",
                    iconPack: "feather",
                    icon: "icon-alert-triangle",
                    text: "The selected item has not been submitted"
                })
            }

            this.$http.post(`/tvd/outbound/deliveryorder/close/${submittedItem.do_id}`, {
                warehouse_id,
                outbound_id
            })
                .then(async res => {
                    if (res && res.data && res.data.success) {
                        this.$vs.notify({
                            color: "success",
                            title: "Success",
                            iconPack: "feather",
                            icon: "icon-check",
                            text: "DO has been closed successfully"
                        })

                        const commitInfoRes = await this.searchByOutbound()
                        if (commitInfoRes && commitInfoRes.data) {
                            this.commitInfo = commitInfoRes.data
                            this.outboundDetail = commitInfoRes.data.outbound || {}
                        }

                        this.isLoading = false
                    }
                })
                .catch(error => {
                    this.handleError(error)
                    this.isLoading = false
                })
        },

        printDO(item) {
            this.isLoading = true

            const driver = this.drivers[item.id]
            const vehicle_code = this.vehicleCodes[item.id]

            this.$http.post(`/tvd/outbound/deliveryorder/print/${item.id}`, {
                driver,
                vehicle_code
            }, {
                responseType: 'blob'
            })
                .then(res => {
                    const blob_file = new Blob([res.data], {
                        type: 'application/pdf'
                    })
                    window.open(URL.createObjectURL(blob_file))

                    this.isLoading = false
                })
                .catch(error => {
                    this.handleError(error)

                    this.isLoading = false
                })
        }
    },

    mounted() {
        this.fetchAllData()
    }
}
</script>

<style>
.vs-tabs-primary button.btn:not(:disabled):hover {
    color: #fff !important;
}
</style>

<style scoped>
.gap-x-3 {
    column-gap: 0.75rem !important;
}

.gap-y-3 {
    row-gap: 0.75rem !important;
}

.gap-x-4 {
    column-gap: 1rem !important;
}

.gap-4 {
    grid-gap: 1rem !important;
    gap: 1rem !important;
}
</style>